import { useMutation, UseMutationResult, useQuery } from 'react-query';
import { useState } from 'react';
import { AggregatedUpdates, Update } from '../../@types/update';
import PaginationResponseDto from '../../interfaces/paginationResponse.interface';
import { getUpdate, updateUpdates } from '../../api/updateApi';
import { RequestQuerySortTypesEnum, UpdateStatusEnum } from '../../consts/enums';

type UseGetUpdates = {
  updates: Update[],
  totalCount?: number,
  loadUpdates: boolean,
  isFetched: boolean,
  aggregatedTotal: number,
  setStatus: (v: UpdateStatusEnum) => void,
  status: UpdateStatusEnum,
  aggregatedUpdates: {[key: string]: AggregatedUpdates},
  refetch: () => void,
}

export const useReadUpdates = (
  onSuccess?: () => void,
):UseMutationResult<unknown, unknown, string[], unknown> => {
  return useMutation((ids) => {
    return updateUpdates(ids, { status: UpdateStatusEnum.Read });
  }, {
    onSuccess,
  });
};

export const useGetUpdates = (): UseGetUpdates => {
  const [status, setStatus] = useState(UpdateStatusEnum.Unread);
  const {
    data,
    isFetching,
    isFetched,
    refetch,
  } = useQuery<PaginationResponseDto<Update>>(
    ['updates', status],
    () => getUpdate({
      where: {
        status: status || undefined,
      },
      limit: 100,
      order: {
        createdAt: RequestQuerySortTypesEnum.Descendant,
      },
    }),
  );

  const aggregatedUpdates = data?.results.reduce((acc, update) => {
    const key = `${update.updateType}_${update.status}_${update.accountId}_${update.positionId}`;
    if (acc[key]) {
      acc[key].aggAmount += 1;
      acc[key].id.push(update.id);
      if (new Date(acc[key].createdAt).getTime() - new Date(update.createdAt).getTime() <= 0) {
        acc[key].createdAt = update.createdAt;
      }

      return acc;
    }

    acc[key] = {
      id: [update.id],
      type: update.updateType,
      status: update.status,
      positionId: update.positionId,
      accountId: update.accountId,
      userName: update.userName,
      userEmail: update.userEmail,
      wasMatchFlowTriggered: update.wasMatchFlowTriggered,
      userId: update.userId,
      matchId: update.matchId,
      jobTitle: update.jobTitle,
      accountName: update.accountName,
      talentName: update.talentName,
      createdAt: update.createdAt,
      aggAmount: 1,
    };

    return acc;
  }, {} as {[key: string]: AggregatedUpdates}) || null;

  return {
    updates: data?.results || [],
    aggregatedUpdates: aggregatedUpdates || {},
    totalCount: data?.totalCount,
    aggregatedTotal: Object.values(aggregatedUpdates || {}).reduce((acc, aggUpdate) => {
      if (aggUpdate.status === UpdateStatusEnum.Unread) {
        acc += 1;
      }

      return acc;
    }, 0) || 0,
    loadUpdates: isFetching,
    isFetched,
    refetch,
    setStatus: (value: UpdateStatusEnum) => setStatus(value),
    status,
  };
};
